// Shared presentational primitives for the card layouts on the positions,
// open-orders and trades surfaces. Pure UI — no data derivation lives here,
// so the table and card paths can't drift on what a number *means*.

import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Bell, BellOff, Grid3x3, Info, LayoutGrid, List, Pin, Rows3 } from 'lucide-react';
import { fmtQty } from './cardFormat';
import { HelpPopover } from '../molecules/HelpPopover';

/** Horizontal scroller with an ALWAYS-VISIBLE scrollbar and edge FADES that
 *  appear only when content actually extends past that edge — the off-screen
 *  card peeks through the gradient as an implicit "more this way" (Brendan,
 *  Jul 9). The scrollbar is OURS (track + draggable thumb below the content;
 *  native is hidden via .g1-hscroll): Chrome 150/macOS keeps overlay
 *  scrollbars that hide at rest no matter what ::-webkit-scrollbar or
 *  scrollbar-width say — verified live — so a persistent bar has to be an
 *  owned element. Callback ref, not an effect: the scroller mounts/unmounts
 *  with its section. */
export function EdgeFadeScroller({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const [edges, setEdges] = useState({ left: false, right: false });
  const [thumb, setThumb] = useState<{ widthPct: number; leftPct: number } | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    if (el.scrollWidth > el.clientWidth + 1) {
      const widthPct = (el.clientWidth / el.scrollWidth) * 100;
      const leftPct = (el.scrollLeft / el.scrollWidth) * 100;
      setThumb((prev) =>
        prev &&
        Math.abs(prev.widthPct - widthPct) < 0.1 &&
        Math.abs(prev.leftPct - leftPct) < 0.1
          ? prev
          : { widthPct, leftPct },
      );
    } else {
      setThumb(null);
    }
  }, []);
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      roRef.current?.disconnect();
      roRef.current = null;
      scrollerRef.current = el;
      if (!el) return;
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      // The content wrapper too — cards/rows appearing changes scrollWidth
      // without touching the scroller's own box.
      if (el.firstElementChild) ro.observe(el.firstElementChild);
      roRef.current = ro;
    },
    [update],
  );
  const fade =
    'pointer-events-none absolute top-0 w-12 transition-opacity duration-200';
  return (
    <div className={`relative ${className}`}>
      {/* pb-1 = the gutter between content and the bar below. */}
      <div ref={attach} onScroll={update} className="g1-hscroll overflow-x-auto pb-1">
        {children}
      </div>
      <div
        aria-hidden="true"
        className={`${fade} bottom-2.5 left-0 bg-gradient-to-r from-background via-background/60 to-transparent ${
          edges.left ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        aria-hidden="true"
        className={`${fade} bottom-2.5 right-0 bg-gradient-to-l from-background via-background/60 to-transparent ${
          edges.right ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {thumb && (
        <div
          ref={trackRef}
          onPointerDown={(e) => {
            // Track click (not thumb) — jump so the click point centers.
            const el = scrollerRef.current;
            const track = trackRef.current;
            if (!el || !track || e.target !== track) return;
            const rect = track.getBoundingClientRect();
            const frac = (e.clientX - rect.left) / rect.width;
            el.scrollLeft = frac * el.scrollWidth - el.clientWidth / 2;
          }}
          className="bg-border/25 relative mt-1 h-1.5 w-full cursor-pointer rounded-full"
        >
          <div
            role="scrollbar"
            aria-controls=""
            aria-orientation="horizontal"
            aria-valuenow={Math.round(thumb.leftPct)}
            onPointerDown={(e) => {
              const el = scrollerRef.current;
              if (!el) return;
              e.preventDefault();
              e.stopPropagation();
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              dragRef.current = { startX: e.clientX, startScrollLeft: el.scrollLeft };
            }}
            onPointerMove={(e) => {
              const d = dragRef.current;
              const el = scrollerRef.current;
              const track = trackRef.current;
              if (!d || !el || !track) return;
              el.scrollLeft =
                d.startScrollLeft +
                (e.clientX - d.startX) * (el.scrollWidth / track.clientWidth);
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
            className="bg-border hover:bg-muted-foreground/60 absolute inset-y-0 cursor-grab rounded-full active:cursor-grabbing"
            style={{ width: `${thumb.widthPct}%`, left: `${thumb.leftPct}%` }}
          />
        </div>
      )}
    </div>
  );
}

const CARD_GRID_VARIANT_CLASS = {
  default: '',
  mini: 'g1-card-grid-cq--mini',
  wide: 'g1-card-grid-cq--wide',
} as const;

/** Responsive grid that holds a set of entity cards. */
export function CardGrid({
  children,
  className = '',
  variant = 'default',
}: {
  children: ReactNode;
  className?: string;
  /** 'mini' = dense auto-fill grid (~12 across on a wide monitor).
   *  'wide'  = roomier auto-fill tracks for cards that carry their own
   *  control regions (the Org Settings member cards). */
  variant?: keyof typeof CARD_GRID_VARIANT_CLASS;
}) {
  return (
    <div
      className={`g1-card-grid-cq ${CARD_GRID_VARIANT_CLASS[variant]} ${className}`}
    >
      <div className="g1-card-grid-cq__grid">{children}</div>
    </div>
  );
}

/** Dense, no-gutter grid for the PositionTracker compact-list view.
 *  The min track width is about two normal position cards, so a wide
 *  six-card row becomes three compact columns. */
export function CompactListGrid({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`g1-compact-list-cq ${className}`}>
      <div className="g1-compact-list-cq__grid">{children}</div>
    </div>
  );
}

/**
 * Same-game highlight — ONE token for every surface: full cards, compact rows,
 * pinned rows, and table `<tr>`s.
 *
 * Every other visual channel on these elements is already load-bearing, and each
 * earlier attempt at this silently destroyed a signal traders rely on:
 *   - background  -> an order row's QUEUE TONE (`bg-green/yellow/red-500/20` plus
 *                    the front-of-book pulse, `queueRowTone`). An `!important`
 *                    tint outranks all four tiers — CSS Cascade L4 puts an
 *                    important author declaration above animations too — so
 *                    hovering a game to check exposure FLATTENED the queue signal
 *                    on that game's orders.
 *   - ring        -> CardShell's `standout` amber ring (an actively-filling order)
 *                    AND the blue focus-visible ring. Both write --tw-ring-color,
 *                    and Tailwind emits cyan after amber, so the highlight won.
 *   - box-shadow  -> `animate-divergence-flash`. Tailwind rings ARE box-shadows.
 *   - border      -> the amber `activeInSale` marker (`border-l-4`) and the
 *                    `standout` border; a border also shifts the row 2px.
 * Hence OUTLINE, which nothing else *paints*. But it is NOT a free channel, and
 * both traps below are load-bearing — do not "tidy" either away:
 *
 * `!` (important) — because `focus:outline-none` sits in the SAME className on
 * every clickable surface (CardShell, CompactPositionItem, CompactOrderItem,
 * pinnedRows), and Tailwind's `outline-none` is NOT `outline-style: none` — it is
 * `outline: 2px solid TRANSPARENT`. At (0,2,0) it outranks `outline-cyan-400/70`
 * at (0,1,0). Without `!`, clicking a card (they're `tabIndex=0`, and the focus
 * ring is focus-VISIBLE so a mouse click leaves no cue) permanently silences that
 * card's highlight: every same-game row lights up except the one you clicked —
 * including when you hover the clicked card itself. Measured in Chrome: 2984 cyan
 * px blurred, 0 focused. `!` on the outline properties ONLY; it must never reach
 * `background`, or we are back to flattening the queue tone.
 *
 * `-outline-offset-4` — the offset is doing two jobs at once, and BOTH bounds are
 * tight. It must be:
 *   - NEGATIVE (inside the element), because an outline drawn outside the border
 *     box is CLIPPED AWAY ENTIRELY by an `overflow-hidden` ancestor — which the
 *     card grids have. Measured at offset 0: the row kept its outline, the card
 *     lost it completely, in both themes. A highlight that silently vanishes on
 *     one surface is the worst failure mode this feature has.
 *   - at least 4px, because the outline paints ON TOP of anything within its
 *     inset. `activeInSale` marks an actively-filling order with `border-l-4`
 *     (4px), and CardShell's `standout` state draws an amber border. At the
 *     natural `-outline-offset-2` the cyan lands exactly on them: measured amber
 *     rgb(251,196,72) -> cyan rgb(99,207,189), ZERO amber pixels surviving. The
 *     trader loses the "you are being filled right now" signal by hovering.
 * -4 clears every border on these surfaces and cannot be clipped. Verified in
 * Chrome across {card, table row} x {dark, light} x {focused, blurred}: cyan
 * outline present AND amber marker intact in all cases.
 *
 * Dark value is the BASE; `light:` supplies the light-theme override — the app
 * only toggles a `.light` class, so `dark:` never matches.
 */
export const SAME_GAME_HIGHLIGHT =
  '!outline !outline-2 !-outline-offset-4 !outline-cyan-400/70 light:!outline-cyan-600/70';

/** The card frame. `standout` draws the amber border used for orders that
 *  are actively filling, so they pop out of the grid. */
export function CardShell({
  standout = false,
  accentClass = '',
  className = '',
  title,
  ariaLabel,
  onClick,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  standout?: boolean;
  accentClass?: string;
  className?: string;
  title?: string;
  ariaLabel?: string;
  onClick?: () => void;
  /** Same-game highlight — the page sets/clears the hovered row. Mouse-only by
   *  design: keyboard/a11y is deliberately out of scope for this ticket and
   *  wants doing properly (focusable `<tr>`s, ARIA), not bolted onto these two
   *  handlers — React focus events bubble, so a nested Cancel/Edit/pin button
   *  would spuriously set or clear the highlight. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}) {
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onClick || e.currentTarget !== e.target) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      title={title}
      role={onClick ? 'link' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`bg-card relative flex flex-col overflow-hidden rounded-lg border p-3 transition-colors ${
        standout
          ? 'border-amber-400 ring-2 ring-amber-400/30'
          : 'border-border hover:border-muted-foreground/30'
      } ${onClick ? 'cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70' : ''} ${className}`}
    >
      {accentClass && (
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute inset-y-0 left-0 w-1 ${accentClass}`}
        />
      )}
      {children}
    </div>
  );
}

/** Card header: two badge clusters (left/right), title, optional subtitle. */
export function CardHeader({
  leftBadges,
  rightBadges,
  title,
  subtitle,
}: {
  leftBadges?: ReactNode;
  rightBadges?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
}) {
  return (
    <div className="mb-2">
      {(leftBadges || rightBadges) && (
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-1">{leftBadges}</div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
            {rightBadges}
          </div>
        </div>
      )}
      {/* truncate + min-w-0 — without these, an unbroken string (e.g. a
          Polymarket asset_id hex when the market lookup hasn't resolved
          yet) would push the card's content box wider than the grid track
          and visually disrupt the row. Tooltip on the wrapper surfaces the
          full string on hover for power users. */}
      <div
        className="text-foreground min-w-0 truncate text-sm font-semibold leading-tight"
        title={typeof title === 'string' ? title : undefined}
      >
        {title}
      </div>
      {subtitle && (
        <div
          className="text-muted-foreground mt-0.5 min-w-0 truncate font-mono text-[11px] leading-tight"
          title={typeof subtitle === 'string' ? subtitle : undefined}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}

function findTeamToken(title: string, team: string): { start: number; end: number } | null {
  const haystack = title.toUpperCase();
  const needle = team.toUpperCase();
  let from = 0;
  while (from < haystack.length) {
    const start = haystack.indexOf(needle, from);
    if (start === -1) return null;
    const end = start + needle.length;
    const before = start === 0 ? '' : haystack[start - 1];
    const after = end >= haystack.length ? '' : haystack[end];
    const beforeOk = before === '' || !/[A-Z0-9]/.test(before);
    const afterOk = after === '' || !/[A-Z0-9]/.test(after);
    if (beforeOk && afterOk) return { start, end };
    from = end;
  }
  return null;
}

/** Highlights the market's contract team inside matchup titles, without using
 * YES/NO colors. A NO on WSH still highlights WSH as the contract team while
 * the side badge carries the red NO semantics. */
export function BetTeamTitle({
  title,
  team,
}: {
  title: string;
  team?: string | null;
}) {
  const match = team ? findTeamToken(title, team) : null;
  if (!match) return <span title={title}>{title}</span>;

  return (
    <span title={title}>
      {title.slice(0, match.start)}
      <span className="rounded bg-primary/15 px-1 text-primary">
        {title.slice(match.start, match.end)}
      </span>
      {title.slice(match.end)}
    </span>
  );
}

/** Card body — grows to equalise card heights so footers line up in a row. */
export function CardBody({ children }: { children: ReactNode }) {
  return <div className="flex-1">{children}</div>;
}

/** Divider + footer region (cost / velocity / age live here). */
export function CardFooter({ children }: { children: ReactNode }) {
  return <div className="border-border/60 mt-2 border-t pt-2">{children}</div>;
}

/** A label/value line inside a card body. */
export function StatLine({
  label,
  value,
  valueClass = '',
  title,
}: {
  label: string;
  value: ReactNode;
  valueClass?: string;
  title?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5" title={title}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={`font-mono text-sm ${valueClass}`}>{value}</span>
    </div>
  );
}

/** Label-over-value cell for the dense compact-item stat rows
 *  (CompactPositionItem / CompactOrderItem). */
export function CompactStat({
  label,
  value,
  valueClass = 'text-foreground',
  title,
}: {
  label: string;
  value: ReactNode;
  valueClass?: string;
  title?: string;
}) {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-muted-foreground truncate text-[10px] leading-none">{label}</div>
      <div className={`mt-0.5 truncate font-mono text-[11px] font-semibold leading-tight ${valueClass}`}>
        {value}
      </div>
    </div>
  );
}

/** Thumbtack toggle for the PositionTracker's Pinned strip. Pinning makes a
 *  WATCH COPY — the item stays in its normal section too — so the tooltip
 *  says so. stopPropagation: a pin click must never fire the row/card
 *  onOpen underneath it. */
export function PinToggle({
  pinned,
  onToggle,
  size = 12,
}: {
  pinned: boolean;
  onToggle: () => void;
  size?: number;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={
        pinned
          ? 'Unpin from the Pinned section'
          : 'Pin to the Pinned section (a watch copy — the item stays here too)'
      }
      aria-pressed={pinned}
      className={`shrink-0 rounded p-1 transition-colors ${
        pinned
          ? 'text-amber-400 hover:bg-amber-400/10'
          : 'text-muted-foreground hover:bg-amber-400/10 hover:text-amber-400'
      }`}
    >
      <Pin size={size} className={pinned ? 'fill-current' : undefined} />
    </button>
  );
}

/** Mute toggle for an order's queue-velocity spike alert. When muted, the
 *  card/row stops flashing on spikes (QueueVelocityTracker.mute). Bell icon so
 *  it reads as "alerting"; struck-through bell-off when silenced. */
export function VelocityMuteToggle({
  muted,
  onToggle,
  size = 12,
}: {
  muted: boolean;
  onToggle: () => void;
  size?: number;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      title={
        muted
          ? 'Velocity spike alerts muted — click to re-enable flashing'
          : 'Mute queue-velocity spike alerts for this order'
      }
      aria-pressed={muted}
      className={`shrink-0 rounded p-1 transition-colors ${
        muted
          ? 'text-muted-foreground hover:bg-amber-400/10 hover:text-amber-400'
          : 'text-amber-400 hover:bg-amber-400/10'
      }`}
    >
      {muted ? <BellOff size={size} /> : <Bell size={size} />}
    </button>
  );
}

/** Contracts a position has resting on open sell orders, labeled "in sale"
 *  (#173 review — the bare amber parenthesized number was only legible via
 *  its tooltip). One atom shared by the position cards, the pinned rows, and
 *  the Open Positions table so the wording can't drift. Self-gates on qty > 0;
 *  the contracts are still HELD until the sells fill, so this annotates qty
 *  rather than reducing it. */
export function PendingSellNote({ qty }: { qty: number }) {
  if (!(qty > 0)) return null;
  return (
    <span
      className="ml-1 text-amber-400"
      title={`${qty} contract${qty === 1 ? '' : 's'} on open sell order${qty === 1 ? '' : 's'} — still held until the sale fills`}
    >
      ({fmtQty(qty)} in sale)
    </span>
  );
}

/** Small uppercase pill badge. */
export function Pill({
  children,
  className = '',
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${className}`}
    >
      {children}
    </span>
  );
}

/** YES/NO side badge, optionally prefixed with the market's named team. */
export function SideBadge({
  side,
  team,
  className = '',
}: {
  side: 'yes' | 'no';
  team?: string | null;
  className?: string;
}) {
  const fullLabel = `${team ? `${team} ` : ''}${side.toUpperCase()}`;
  return (
    <Pill
      title={fullLabel}
      className={`min-w-0 max-w-full overflow-hidden whitespace-nowrap ${
        side === 'yes'
          ? 'bg-green-500/20 text-green-400'
          : 'bg-red-500/20 text-red-400'
      } ${className}`}
    >
      {team && <span className="min-w-0 truncate">{team}</span>}
      <span className="shrink-0">{side.toUpperCase()}</span>
    </Pill>
  );
}

/** One tile in the summary strip above a card grid (e.g. "open orders 9"). */
export function SummaryTile({
  label,
  value,
  tone = 'default',
  title,
}: {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'positive' | 'warning' | 'danger';
  title?: string;
}) {
  const toneClass =
    tone === 'positive'
      ? 'text-positive'
      : tone === 'warning'
        ? 'text-amber-400'
        : tone === 'danger'
          ? 'text-negative'
          : 'text-foreground';
  return (
    <div className="bg-card border-border rounded-lg border px-4 py-3" title={title}>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

/** Strip wrapper for SummaryTiles. */
export function SummaryStrip({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-3">
      {children}
    </div>
  );
}

type ViewModeToggleValue = 'compact' | 'table' | 'card' | 'mini';

const VIEW_MODE_META: Record<ViewModeToggleValue, {
  title: string;
  ariaLabel: string;
  icon: typeof List;
}> = {
  compact: {
    title: 'Compact list view',
    ariaLabel: 'Compact list view',
    icon: List,
  },
  table: {
    title: 'Table view',
    ariaLabel: 'Table view',
    icon: Rows3,
  },
  card: {
    title: 'Card view',
    ariaLabel: 'Card view',
    icon: LayoutGrid,
  },
  mini: {
    title: 'Mini card view (dense)',
    ariaLabel: 'Mini card view',
    icon: Grid3x3,
  },
};

/** Segmented view-mode toggle for dense list / table / card surfaces. */
export function ViewModeToggle<T extends ViewModeToggleValue>({
  value,
  onChange,
  modes,
}: {
  value: T;
  onChange: (m: T) => void;
  modes: readonly T[];
}) {
  const btn = (active: boolean) =>
    `flex items-center justify-center px-2 py-1 transition-colors ${
      active
        ? 'bg-blue-500/20 text-blue-400'
        : 'text-muted-foreground hover:text-foreground'
    }`;
  return (
    <div className="border-border inline-flex overflow-hidden rounded-md border">
      {modes.map((mode) => {
        const meta = VIEW_MODE_META[mode];
        const Icon = meta.icon;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => onChange(mode)}
            className={btn(value === mode)}
            title={meta.title}
            aria-label={meta.ariaLabel}
          >
            <Icon size={14} />
          </button>
        );
      })}
    </div>
  );
}

/** Small info icon that toggles a click-dismissed popover with section help.
 *  Use next to a section header (Open Positions / Open Orders / My Trades)
 *  to explain the columns/badges/colors. Dismissed on outside-click or Esc.
 *
 *  A wrapper over `HelpPopover` since #602 — the popover mechanics (portal,
 *  viewport clamping, the shared `useDismissibleLayer` dismissal contract) are
 *  now shared with the fee-uncertainty marker on price pills rather than living
 *  here. The icon, the panel and the copy are unchanged. */
export function InfoTooltip({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <HelpPopover
      label={title ? `About ${title}` : 'About this section'}
      title={title}
      trigger={<Info size={14} />}
      triggerClassName="text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded p-0.5 transition-colors"
    >
      {children}
    </HelpPopover>
  );
}
